import { AnalysisResult } from '../AnalysisResult';
import type { TeoText } from '../TeoText';
import { HistoryEntry, type HistoryData } from './HistoryEntry';

export class HistoryManager {
  #text: TeoText;
  #history: HistoryEntry[] = [];
  #viewIsActive = false;

  constructor(text: TeoText) {
    this.#text = text;
    text.store.addEventListener('view', (view) => {
      if (view === 'versions') {
        this.viewIsActive = true;
      } else {
        this.viewIsActive = false;
      }
    });
  }

  get entries(): HistoryEntry[] {
    return this.#history;
  }

  set viewIsActive(active: boolean) {
    if (this.#viewIsActive === active) return;
    this.#viewIsActive = active;
    this.#text.isEditable = !active;
    if (active) {
      this.#history.at(0)?.setTextDiff();
    } else {
      this.#text.diff.hide();
    }
  }

  get viewIsActive(): boolean {
    return this.#viewIsActive;
  }

  /**
   * Sets the visible text to the given history entry.
   * @param data The history entry to restore.
   * @param virtual If true, the entry is not part of the history and is only used to restore the text.
   */
  select(data: HistoryData): void {
    this.changeTextFromEntry(data);
  }

  /**
   * Restores the text to the given history entry.
   * @param data The history entry to restore or null to restore to the "current" text.
   * @param virtual If true, the entry is not part of the history and is only used to restore the text.
   */
  restore(data: HistoryData): void {
    this.#text.updateTextData(data);
    // this.changeTextFromEntry(data);
    // this.addHistoryEntry();
    // setTimeout(() => {
    //   // after view change
    //   this.#text.analyze({ trigger: null });
    // }, 200);
  }

  /**
   * Adds a new history entry when the text is analyzed.
   * @param analysis The analysis result to save in the history.
   */
  addHistoryEntry(): void {
    try {
      const entry = HistoryEntry.create(this.#text);
      this.#history.unshift(entry);
      this.#text.events.triggerEvent('historyChanged', this.#history);
      this.#text.save();
    } catch (error) {
      this.#text.core.notifications.addError(error);
    }
  }

  private changeTextFromEntry(data: HistoryData): void {
    this.#text.isEditable = true;
    this.#text.text = data.text;
    this.#text.serialized = data.serialized;
    this.#text.analysisConfiguration = data.analysisConfiguration;
    this.#text.lastEdited = data.lastEdited;
    this.#text.analysis = data.analysis ? AnalysisResult.fromJSON(this.#text, data.analysis) : null;

    //     TeoCore.current.replaceTextWithTextData(data.id, data);
    this.#text.isEditable = !this.#viewIsActive;

    // TODO: check if config is different first
    this.#text.events.triggerEvent('configChanged', data.analysisConfiguration ?? {});
    // this.#text.events.triggerEvent('editable', false);
    this.#text.events.triggerEvent('textChangedExternal', undefined);

    // TODO: check if analysis is different first
    if (this.#text.analysis) this.#text.events.triggerEvent('gotAnalysis', this.#text.analysis);
    // TODO: check if analysis state is different first
    this.#text.events.triggerEvent('analysisStateChanged', undefined);
  }

  toJSON() {
    return this.#history.map((entry) => entry.toJSON());
  }

  restoreFromJSON(json: HistoryJSON) {
    this.#history = json.map((data) => HistoryEntry.fromJSON(this.#text, data));
    return this;
  }
}

export type HistoryJSON = ReturnType<HistoryManager['toJSON']>;
